import { useEffect, useState } from "react";
import {
  CalendarClock,
  CalendarOff,
  Plus,
  Trash2,
  TriangleAlert,
  X,
} from "lucide-react";

import type {
  DispatchRun,
  DispatchRunSchedule,
  RunScheduleWeekday,
} from "@/api/carrier";
import { DetailDrawer } from "@/components/detail-drawer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

const WEEKDAYS: {
  key: RunScheduleWeekday;
  label: string;
  fullLabel: string;
}[] = [
  { key: "mon", label: "Mon", fullLabel: "Monday" },
  { key: "tue", label: "Tue", fullLabel: "Tuesday" },
  { key: "wed", label: "Wed", fullLabel: "Wednesday" },
  { key: "thu", label: "Thu", fullLabel: "Thursday" },
  { key: "fri", label: "Fri", fullLabel: "Friday" },
  { key: "sat", label: "Sat", fullLabel: "Saturday" },
  { key: "sun", label: "Sun", fullLabel: "Sunday" },
];

const EMPTY_WEEKLY: Record<RunScheduleWeekday, DispatchRun[]> = {
  mon: [],
  tue: [],
  wed: [],
  thu: [],
  fri: [],
  sat: [],
  sun: [],
};

const GROUP_COLORS = [
  "border-amber-300 bg-amber-50 text-amber-900",
  "border-blue-300 bg-blue-50 text-blue-900",
  "border-purple-300 bg-purple-50 text-purple-900",
  "border-emerald-300 bg-emerald-50 text-emerald-900",
  "border-rose-300 bg-rose-50 text-rose-900",
  "border-cyan-300 bg-cyan-50 text-cyan-900",
];

interface RunGroup {
  key: string;
  departs: string;
  returns: string;
  days: RunScheduleWeekday[];
  idsByDay: Partial<Record<RunScheduleWeekday, string>>;
}

interface RunDraft {
  originalKey: string | null;
  departs: string;
  returns: string;
  days: Set<RunScheduleWeekday>;
  idsByDay: Partial<Record<RunScheduleWeekday, string>>;
}

export function RunScheduleEditor({
  value,
  onChange,
  onDraftDirtyChange,
}: {
  value: DispatchRunSchedule | null;
  onChange: (value: DispatchRunSchedule | null) => void;
  onDraftDirtyChange?: (dirty: boolean) => void;
}) {
  const [overrideDate, setOverrideDate] = useState("");
  const [draft, setDraft] = useState<RunDraft | null>(null);
  const [draftBaseline, setDraftBaseline] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const draftDirty = draft !== null && serializeDraft(draft) !== draftBaseline;

  useEffect(() => {
    onDraftDirtyChange?.(draftDirty);
  }, [draftDirty, onDraftDirtyChange]);

  if (!value) {
    return (
      <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3">
        <div className="flex items-center gap-2 text-amber-800">
          <TriangleAlert className="h-4 w-4 shrink-0" />
          <span className="text-sm font-medium">
            Delivery estimates are off for this dispatch center
          </span>
        </div>
        <p className="text-xs text-amber-700">
          Without a run schedule, customers see business-day guidance instead of
          time estimates.
        </p>
        <Button
          variant="outline"
          size="touch"
          className="bg-background"
          onClick={() =>
            onChange({ weekly: { ...EMPTY_WEEKLY }, overrides: {} })
          }
        >
          <Plus className="h-4 w-4" />
          Set up run schedule
        </Button>
      </div>
    );
  }

  const groups = groupRuns(value.weekly);
  const replacementOverrideCount = Object.values(value.overrides).filter(
    (runs) => runs.length > 0
  ).length;
  const colorOf = (groupKey: string) => {
    const index = groups.findIndex((group) => group.key === groupKey);
    return GROUP_COLORS[Math.max(index, 0) % GROUP_COLORS.length];
  };

  const startAdd = (day: RunScheduleWeekday) => {
    setDraftError(null);
    const nextDraft: RunDraft = {
      originalKey: null,
      departs: "12:00",
      returns: "20:00",
      days: new Set([day]),
      idsByDay: {},
    };
    setDraft(nextDraft);
    setDraftBaseline(serializeDraft(nextDraft));
  };

  const startEdit = (group: RunGroup) => {
    setDraftError(null);
    const nextDraft: RunDraft = {
      originalKey: group.key,
      departs: group.departs,
      returns: group.returns,
      days: new Set(group.days),
      idsByDay: { ...group.idsByDay },
    };
    setDraft(nextDraft);
    setDraftBaseline(serializeDraft(nextDraft));
  };

  const clearDraft = () => {
    setDraft(null);
    setDraftBaseline(null);
    setDraftError(null);
  };

  const closeDraft = () => {
    if (draftDirty && !window.confirm("Discard your unsaved run changes?")) {
      return;
    }
    clearDraft();
  };

  const commitDraft = () => {
    if (!draft) return;
    const validation = validateRunDraft(draft);
    if (validation) {
      setDraftError(validation);
      return;
    }
    const remaining = groups.filter((group) => group.key !== draft.originalKey);
    const edited: RunGroup = {
      key: `${draft.departs}|${draft.returns}`,
      departs: draft.departs,
      returns: draft.returns,
      days: WEEKDAYS.map((day) => day.key).filter((day) => draft.days.has(day)),
      idsByDay: draft.idsByDay,
    };
    onChange({
      ...value,
      weekly: expandGroups(coalesceGroups([...remaining, edited])),
    });
    clearDraft();
  };

  const deleteDraftGroup = () => {
    if (!draft) return;
    onChange({
      ...value,
      weekly: expandGroups(
        groups.filter((group) => group.key !== draft.originalKey)
      ),
    });
    clearDraft();
  };

  const toggleDraftDay = (day: RunScheduleWeekday) => {
    if (!draft) return;
    const days = new Set(draft.days);
    if (days.has(day)) days.delete(day);
    else days.add(day);
    setDraft({ ...draft, days });
    setDraftError(null);
  };

  const addOverride = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(overrideDate)) return;
    onChange({
      ...value,
      overrides: { ...value.overrides, [overrideDate]: [] },
    });
    setOverrideDate("");
  };

  const removeOverride = (date: string) => {
    const overrides = { ...value.overrides };
    delete overrides[date];
    onChange({ ...value, overrides });
  };

  return (
    <div className="space-y-4">
      {groups.length === 0 && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800">
          <TriangleAlert className="h-4 w-4 shrink-0" />
          <span className="text-xs">
            {replacementOverrideCount > 0
              ? `No regular weekly runs — date-specific replacements still apply on ${replacementOverrideCount} ${replacementOverrideCount === 1 ? "date" : "dates"}.`
              : "No runs configured — estimates stay off until at least one regular or date-specific run exists."}
          </span>
        </div>
      )}

      {groups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Runs:</span>
          {groups.map((group) => (
            <Button
              key={group.key}
              type="button"
              variant="outline"
              size="sm-touch"
              onClick={() => startEdit(group)}
              className={colorOf(group.key)}
            >
              {formatRunRange(group)}
            </Button>
          ))}
        </div>
      )}

      <div className="space-y-2 md:hidden">
        {WEEKDAYS.map(({ key, fullLabel }) => {
          const dayGroups = groups.filter((group) => group.days.includes(key));
          return (
            <div key={key} className="rounded-md border p-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium">{fullLabel}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm-touch"
                  aria-label={`Add run on ${fullLabel}`}
                  onClick={() => startAdd(key)}
                >
                  <Plus className="h-4 w-4" />
                  Add run
                </Button>
              </div>
              {dayGroups.length === 0 ? (
                <p className="mt-2 text-xs text-muted-foreground">No runs</p>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                  {dayGroups.map((group) => (
                    <Button
                      key={group.key}
                      type="button"
                      variant="outline"
                      size="sm-touch"
                      className={colorOf(group.key)}
                      aria-label={`${fullLabel} run ${compactTime(group.departs)} to ${compactTime(group.returns)}`}
                      onClick={() => startEdit(group)}
                    >
                      {formatRunRange(group)}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="hidden grid-cols-7 gap-1 rounded-md border p-1 md:grid">
        {WEEKDAYS.map(({ key, label }) => (
          <div
            key={key}
            className="py-1 text-center text-xs font-medium text-muted-foreground"
          >
            {label}
          </div>
        ))}
        {WEEKDAYS.map(({ key, label, fullLabel }) => {
          const dayGroups = groups.filter((group) => group.days.includes(key));
          return (
            <div
              key={key}
              className="flex min-h-24 flex-col gap-1 rounded border border-dashed bg-muted/40 p-1"
            >
              {dayGroups.length === 0 && (
                <span className="pt-1 text-center text-[10px] text-muted-foreground">
                  No runs
                </span>
              )}
              {dayGroups.map((group) => (
                <Button
                  key={group.key}
                  type="button"
                  variant="outline"
                  size="sm"
                  className={`h-auto min-h-8 w-full whitespace-normal px-1 py-1 text-left text-[10px] leading-tight ${colorOf(group.key)}`}
                  aria-label={`${label} run ${compactTime(group.departs)} to ${compactTime(group.returns)}`}
                  onClick={() => startEdit(group)}
                >
                  {formatRunRange(group)}
                </Button>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-auto h-8 border border-dashed px-1 text-[10px]"
                aria-label={`Add run on ${fullLabel}`}
                onClick={() => startAdd(key)}
              >
                + Add
              </Button>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">
        Departs is the load deadline. Returns is when the trip is back at the
        dispatch center; use values above 24:00 for overnight returns.
      </p>

      <div className="space-y-2 border-t pt-3">
        <h4 className="text-sm font-medium">Date overrides</h4>
        <p className="text-xs text-muted-foreground">
          Overrides replace the regular runs for one date. Add an empty override
          for a holiday or other no-run day.
        </p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(value.overrides)
            .sort(([first], [second]) => first.localeCompare(second))
            .map(([date, runs]) => (
              <span
                key={date}
                className="inline-flex min-h-11 items-center gap-2 rounded-md border bg-muted px-3 text-xs md:min-h-8"
              >
                {runs.length === 0 ? (
                  <CalendarOff className="h-4 w-4 shrink-0" />
                ) : (
                  <CalendarClock className="h-4 w-4 shrink-0" />
                )}
                <span>
                  {date} ·{" "}
                  {runs.length === 0
                    ? "No runs"
                    : `${runs.length} replacement ${runs.length === 1 ? "run" : "runs"}: ${runs.map(formatOverrideRun).join(", ")}`}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm-touch"
                  onClick={() => removeOverride(date)}
                  aria-label={`Remove override ${date}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </span>
            ))}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            type="date"
            className="w-full sm:w-48"
            value={overrideDate}
            onChange={(event) => setOverrideDate(event.target.value)}
            aria-label="Override date"
          />
          <Button
            variant="outline"
            size="touch"
            className="w-full sm:w-auto"
            onClick={addOverride}
            disabled={!overrideDate}
          >
            <Plus className="h-4 w-4" />
            Add no-run date
          </Button>
        </div>
      </div>

      <Button
        type="button"
        variant="ghost"
        size="touch"
        className="px-2 text-destructive hover:text-destructive"
        onClick={() => onChange(null)}
      >
        Remove schedule (disable estimates)
      </Button>

      {draft && (
        <DetailDrawer
          open
          onOpenChange={(open) => {
            if (!open) closeDraft();
          }}
          title={draft.originalKey ? "Edit run" : "New run"}
          description="Set departure, return, and every weekday this run follows."
        >
          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <label
                htmlFor="run-departs"
                className="space-y-1 text-sm font-medium"
              >
                <span>Departs (load deadline)</span>
                <Input
                  id="run-departs"
                  aria-label="Run departs"
                  placeholder="12:00"
                  inputMode="numeric"
                  className="font-mono"
                  value={draft.departs}
                  onChange={(event) => {
                    setDraft({ ...draft, departs: event.target.value });
                    setDraftError(null);
                  }}
                />
              </label>
              <label
                htmlFor="run-returns"
                className="space-y-1 text-sm font-medium"
              >
                <span>Returns</span>
                <Input
                  id="run-returns"
                  aria-label="Run returns"
                  placeholder="20:00 or 25:00"
                  inputMode="numeric"
                  className="font-mono"
                  value={draft.returns}
                  onChange={(event) => {
                    setDraft({ ...draft, returns: event.target.value });
                    setDraftError(null);
                  }}
                />
              </label>
            </div>

            <fieldset className="space-y-1">
              <legend className="mb-1 text-sm font-medium">
                Runs on these days
              </legend>
              {WEEKDAYS.map(({ key, fullLabel }) => (
                <label
                  key={key}
                  htmlFor={`run-day-${key}`}
                  className="flex min-h-11 items-center gap-3 text-sm md:min-h-0"
                >
                  <Checkbox
                    id={`run-day-${key}`}
                    checked={draft.days.has(key)}
                    onCheckedChange={() => toggleDraftDay(key)}
                  />
                  {fullLabel}
                </label>
              ))}
            </fieldset>

            {draftError && (
              <p role="alert" className="text-sm text-destructive">
                {draftError}
              </p>
            )}

            <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              {draft.originalKey ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="touch"
                  className="text-destructive hover:text-destructive"
                  onClick={deleteDraftGroup}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete run
                </Button>
              ) : (
                <span />
              )}
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  size="touch"
                  onClick={closeDraft}
                >
                  Cancel
                </Button>
                <Button type="button" size="touch" onClick={commitDraft}>
                  Apply run
                </Button>
              </div>
            </div>
          </div>
        </DetailDrawer>
      )}
    </div>
  );
}

function validateRunDraft(draft: RunDraft): string | null {
  if (draft.days.size === 0) return "Select at least one weekday.";
  const departs = scheduleMinutes(draft.departs);
  const returns = scheduleMinutes(draft.returns);
  if (departs == null || returns == null) {
    return "Enter departure and return times as HH:MM with valid minutes.";
  }
  if (returns <= departs) return "Return time must be after departure time.";
  return null;
}

function serializeDraft(draft: RunDraft): string {
  return JSON.stringify({
    originalKey: draft.originalKey,
    departs: draft.departs,
    returns: draft.returns,
    days: [...draft.days].sort(),
  });
}

function scheduleMinutes(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return minutes < 60 ? hours * 60 + minutes : null;
}

function newRunId(day: RunScheduleWeekday): string {
  return `${day}-${Math.random().toString(36).slice(2, 8)}`;
}

function compactTime(value: string): string {
  const total = scheduleMinutes(value);
  if (total == null) return value;
  const dayOffset = Math.floor(total / (24 * 60));
  const minutesInDay = total % (24 * 60);
  const hours = Math.floor(minutesInDay / 60);
  const minutes = minutesInDay % 60;
  const period = hours >= 12 ? "PM" : "AM";
  const displayHour = hours % 12 || 12;
  const time =
    minutes === 0
      ? `${displayHour}${period}`
      : `${displayHour}:${String(minutes).padStart(2, "0")}${period}`;
  return dayOffset > 0 ? `${time} +${dayOffset}d` : time;
}

function formatRunRange(group: RunGroup): string {
  return `${compactTime(group.departs)}–${compactTime(group.returns)}`;
}

function formatOverrideRun(run: DispatchRun): string {
  return `${compactTime(run.departs)}–${compactTime(run.returns)}`;
}

function groupRuns(
  weekly: Record<RunScheduleWeekday, DispatchRun[]>
): RunGroup[] {
  const groups = new Map<string, RunGroup>();
  for (const { key: day } of WEEKDAYS) {
    for (const run of weekly[day]) {
      const key = `${run.departs}|${run.returns}`;
      const existing = groups.get(key);
      if (existing) {
        existing.days.push(day);
        existing.idsByDay[day] = run.id;
      } else {
        groups.set(key, {
          key,
          departs: run.departs,
          returns: run.returns,
          days: [day],
          idsByDay: { [day]: run.id },
        });
      }
    }
  }
  return [...groups.values()].sort(
    (first, second) =>
      (scheduleMinutes(first.departs) ?? 0) -
      (scheduleMinutes(second.departs) ?? 0)
  );
}

function expandGroups(
  groups: RunGroup[]
): Record<RunScheduleWeekday, DispatchRun[]> {
  const weekly: Record<RunScheduleWeekday, DispatchRun[]> = {
    mon: [],
    tue: [],
    wed: [],
    thu: [],
    fri: [],
    sat: [],
    sun: [],
  };
  for (const group of groups) {
    for (const day of group.days) {
      weekly[day].push({
        id: group.idsByDay[day] ?? newRunId(day),
        departs: group.departs,
        returns: group.returns,
      });
    }
  }
  for (const { key } of WEEKDAYS) {
    weekly[key].sort(
      (first, second) =>
        (scheduleMinutes(first.departs) ?? 0) -
        (scheduleMinutes(second.departs) ?? 0)
    );
  }
  return weekly;
}

function coalesceGroups(groups: RunGroup[]): RunGroup[] {
  const merged = new Map<string, RunGroup>();
  for (const group of groups) {
    const existing = merged.get(group.key);
    if (!existing) {
      merged.set(group.key, {
        ...group,
        days: [...group.days],
        idsByDay: { ...group.idsByDay },
      });
      continue;
    }
    for (const day of group.days) {
      if (!existing.days.includes(day)) existing.days.push(day);
      existing.idsByDay[day] = existing.idsByDay[day] ?? group.idsByDay[day];
    }
  }
  return [...merged.values()];
}
